import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Stack,
  Breadcrumbs,
  Link,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import jsPDF from "jspdf";
import api from "../api/axios";
import socket from "../socket";
import { CheckCircle } from "@mui/icons-material";
import ReviewDialog from "../components/reviews/ReviewDialog";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [openReview, setOpenReview] = useState(false);
  const [reviewCtx, setReviewCtx] = useState(null);

  useEffect(() => {
    socket.emit("join_order", orderId);
    socket.on("order_status_updated", (data) => {
      if (data.orderId === orderId) {
        setOrder((prev) => ({
          ...prev,
          status: data.status,
          tracking: data.tracking,
        }));
      }
    });
    return () => socket.off("order_status_updated");
  }, [orderId]);

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((res) => setOrder(res.data.order))
      .catch(() => setOrder(null));
  }, [orderId]);

  const openReviewDialog = async (productId) => {
    const res = await api.get(
      `/reviews/eligibility/${productId}/${order._id}`
    );

    setReviewCtx({
      productId,
      orderId: order._id,
      mode: res.data.mode,
      reviewId: res.data.reviewId,
    });

    setOpenReview(true);
  };

  if (!order) return <Typography>Order not found</Typography>;

  const downloadInvoice = () => {
    const pdf = new jsPDF();
    pdf.text("INVOICE", 20, 20);
    pdf.text(`Order ID: ${order._id}`, 20, 30);
    pdf.text(
      `Date: ${new Date(order.createdAt).toLocaleDateString()}`,
      20,
      40
    );
    pdf.text(`Payment: ${order.paymentMethod}`, 20, 50);
    pdf.save(`Invoice_${order._id}.pdf`);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/customer/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Customer Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            href="/customer/orders"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/orders");
            }}
          >
            Total Orders
          </Link>
          <Typography color="text.primary">
            Order Details
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Order Summary */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight={700} mb={1}>
          Order Details
        </Typography>

        <Typography fontSize={14}>
          <b>Order ID:</b> {order._id}
        </Typography>
        <Typography fontSize={14}>
          <b>Order Date:</b>{" "}
          {new Date(order.createdAt).toLocaleDateString()}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {order.items.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "flex-start",
            }}
          >
            {/* Product Image */}
            <Box
              sx={{
                flexShrink: 0,
                width: 80,
                height: 80,
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e0e0e0",
              }}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No Image
                </Typography>
              )}
            </Box>

            {/* Item Details */}
            <Box>
              <Typography fontWeight={600}>
                {item.title}
              </Typography>
              <Typography fontSize={14}>
                Qty: {item.qty} · ₹
                {Number(item.price * item.qty).toLocaleString("en-IN")}
              </Typography>

              {order.status === "DELIVERED" && (
                <Button
                  size="small"
                  sx={{
                    textTransform: "none",
                    color: "#007185",
                    fontWeight: 600,
                    mt: 0.5,
                  }}
                  onClick={() =>
                    openReviewDialog(item.productId)
                  }
                >
                  Write / Edit Review
                </Button>
              )}
            </Box>
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography fontWeight={700}>
          Order Total: ₹
          {Number(order.total).toLocaleString("en-IN")}
        </Typography>

        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={downloadInvoice}
        >
          Download Invoice
        </Button>
      </Paper>

      {/* Tracking */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography fontWeight={700} mb={2}>
          Order Tracking
        </Typography>

        <Stack spacing={1}>
          {order.tracking &&
            order.tracking.map((track, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: 1.5,
                  backgroundColor: track.completed ? "#f0f7ff" : "#fafafa",
                  borderRadius: 1,
                  borderLeft: track.completed ? "4px solid #4caf50" : "4px solid #ccc",
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 20,
                    color: track.completed ? "#4caf50" : "#ccc",
                    mr: 2,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {track.label}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(track.time).toLocaleString()}
                  </Typography>
                </Box>
                {track.completed && (
                  <Chip label="Completed" color="success" size="small" />
                )}
              </Box>
            ))}
        </Stack>
      </Paper>

      {reviewCtx && (
        <ReviewDialog
          open={openReview}
          onClose={() => setOpenReview(false)}
          productId={reviewCtx.productId}
          orderId={reviewCtx.orderId}
          mode={reviewCtx.mode}
          reviewId={reviewCtx.reviewId}
        />
      )}
    </Box>
  );
};

export default OrderDetails;
